/**
 * Safe Web Storage access — survives restrictive / high-security browsers.
 *
 * In several real-world environments `localStorage` / `sessionStorage`
 * are present on `window` but throw the moment you touch them, or are
 * missing entirely:
 *
 *   - Safari "Private Browsing" (older versions throw QuotaExceededError
 *     on the first `setItem`).
 *   - Browsers with "block third-party cookies" / "block all storage"
 *     privacy settings, and locked-down enterprise / kiosk policies.
 *   - `file://` pages, some embedded WebViews, and SSR where the globals
 *     don't exist at all.
 *
 * A bare `sessionStorage.getItem(...)` in any of those contexts throws a
 * `SecurityError` and — because our auth guards, interceptors and the
 * root-route resolver read storage on the very first paint — takes the
 * whole app down with a white screen.
 *
 * This module wraps every access in a `try/catch` and transparently
 * falls back to an in-memory store, so the app keeps working for the
 * lifetime of the tab even when persistence is denied. The API mirrors
 * the Web Storage methods we actually use (`getItem`, `setItem`,
 * `removeItem`) plus an `isStorageAvailable()` probe.
 *
 * Functions (not a service) on purpose: the call sites include the
 * module-level `rootRedirectTo` resolver and the functional guards /
 * interceptors, which run outside Angular's injection context.
 */

export type WebStorageType = 'local' | 'session';

/** In-memory fallback, one bucket per storage type, used when the real
 * backing store is unavailable or throws. Lives for the tab session. */
const memoryFallback: Record<WebStorageType, Map<string, string>> = {
  local: new Map<string, string>(),
  session: new Map<string, string>(),
};

/** Cache of the availability probe so we don't repeatedly trigger the
 * (potentially throwing) access path on every call. */
const availabilityCache: Partial<Record<WebStorageType, boolean>> = {};

function nativeStorage(type: WebStorageType): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return type === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    // Accessing the property itself can throw under strict policies.
    return null;
  }
}

/**
 * Returns true when the real Web Storage backend can be written to and
 * read from. The result is cached. A negative result means callers are
 * transparently using the in-memory fallback.
 */
export function isStorageAvailable(type: WebStorageType): boolean {
  if (availabilityCache[type] !== undefined) return availabilityCache[type] as boolean;
  const store = nativeStorage(type);
  if (!store) return (availabilityCache[type] = false);
  try {
    const probe = '__egl_storage_probe__';
    store.setItem(probe, '1');
    store.removeItem(probe);
    return (availabilityCache[type] = true);
  } catch {
    return (availabilityCache[type] = false);
  }
}

/**
 * Reads a key. When the native backend is available it is authoritative
 * (a real `null` means "not set", and is returned as-is). The in-memory
 * fallback is consulted only when the backend is missing or its read
 * throws — the locked-down case where a prior `safeSetItem` could only
 * land in memory.
 */
export function safeGetItem(type: WebStorageType, key: string): string | null {
  const store = nativeStorage(type);
  if (store) {
    try {
      return store.getItem(key);
    } catch {
      // fall through to memory
    }
  }
  return memoryFallback[type].has(key) ? (memoryFallback[type].get(key) as string) : null;
}

/**
 * Writes a key. Returns true if it reached the real backend, false if it
 * was kept only in memory (e.g. private mode or quota denied). The
 * in-memory copy is always updated so a later read in the same tab is
 * consistent.
 */
export function safeSetItem(type: WebStorageType, key: string, value: string): boolean {
  memoryFallback[type].set(key, value);
  const store = nativeStorage(type);
  if (!store) return false;
  try {
    store.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

/** Removes a key from both the real backend (if any) and the fallback. */
export function safeRemoveItem(type: WebStorageType, key: string): void {
  memoryFallback[type].delete(key);
  const store = nativeStorage(type);
  if (!store) return;
  try {
    store.removeItem(key);
  } catch {
    // already removed from the fallback; nothing else to do
  }
}
